import '@components/module-components/POSMap/pos-map.scss'
import React, {Fragment, useCallback, useState, useEffect, useMemo} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {
  formatSequentialRanges,
  getCenterOfMarkers,
  getNestedConnectedOrders,
  getProcessLatLng,
  groupByProcessPosition,
  sortObjectProperties
} from '@components/module-components/POSMap/helper'
import {createSearchQuery, getParams, getQueryParams, translate} from '@src/helpers'
import _ from 'lodash-es'
import {useDispatch, useSelector} from 'react-redux'
import {GoogleMap, Marker, useJsApiLoader} from '@react-google-maps/api'

import markerIconGrayV2 from '@assets/images/icons/icon_marker_gray.png'
import markerIconYellowV2 from '@assets/images/icons/icon_marker_yellow.png'
import markerIconGreenV2 from '@assets/images/icons/icon_marker_green.png'
import markerIconMultiple from '@assets/images/icons/icon_marker_group.png'

import ProcessesMarkers from '@components/module-components/POSMap/ProcessesMarkers'
import ProcessMarker, {isMarkerClickable} from '@components/module-components/POSMap/ProcessMarker'

import {ENV} from '@environments/environment'
import {setPreviousUrl} from '@redux/actions/navigatorAction'
import VerificationLegendBadge from '@components/VerificationLegend/VerificationLegendBadge'
import {E_ProcessClaimedStatus} from '@src/types'
import {
  IMap,
  MapCenter,
  ProcessData,
  ProcessStatusMap,
  POSMapProps,
  MarkerOptions
} from '@components/module-components/POSMap/types'
import ContentMarkerUnclaimed from '@components/module-components/POSMap/helper/ContentMarkerUnclaimed'
import ContentMarkerSingle from '@components/module-components/POSMap/helper/ContentMarkerSingle'
import ContentMarkerMultiple from '@components/module-components/POSMap/helper/ContentMarkerMultiple'
import ReactDOM from 'react-dom'
import VerifiedLabelMarkup from '@components/module-components/POSMap/helper/VerifiedLabelMarkup'

const {GG_KEY} = ENV

const mapLibraries: ('core' | 'maps' | 'places' | 'geocoding' | 'routes' | 'marker' | 'geometry' | 'elevation' | 'streetView' | 'journeySharing' | 'drawing' | 'visualization')[] = ['geometry']

const TIMEOUT_WAITING = 2500
const TIMEOUT_FINISH = 150

const POSMapV2: React.FC<POSMapProps> = ({data: dataMap}) => {
  const {isLoaded} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GG_KEY,
    libraries: mapLibraries
  })

  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const langData = useSelector((state: any) => state.currentLanguage)
  const [locRedirect, setLocRedirect] = useState<string>('')

  const [processes, setProcesses] = useState<ProcessData[][]>([])
  const [markers, setMarkers] = useState<ProcessData[]>([])
  const [markersArr, setMarkersArr] = useState<ProcessData[]>([])

  const [locHover, setLocHover] = useState<number | null>(null)
  const [locClick, setLocClick] = useState<number | null>(null)
  const [zoomLevel, setZoomLevel] = useState<number>(16)
  const [center, setCenter] = useState<MapCenter | null>(
    dataMap[0]?.process_lat && dataMap[0]?.process_long
      ? {
        lat: Number(dataMap[0].process_lat),
        lng: Number(dataMap[0].process_long)
      }
      : null
  )
  const [infoboxTimer, setInfoboxTimer] = useState<number | null>(null)

  const sortedDataMap = useMemo(() => {
    return [...dataMap].sort((a: IMap, b: IMap) => a.process_position_b2c - b.process_position_b2c)
  }, [dataMap])

  const {order = '', brand = ''} = getParams()

  const onClickProcessV2 = useCallback((process: ProcessData): void => {
    try {
      if (!process.b2c_url) return

      const qs = createSearchQuery({...getQueryParams(process.b2c_url), brand})

      dispatch(setPreviousUrl({
        previous: {...location},
        query: {
          viewMode: 'mapView',
          process_position: process.custom_order
        }
      }))

      history.push(`/step-detail?${qs}`)
    } catch (e) {
      console.error(e)
    }
  }, [brand, dispatch, history, location])

  const onLoad = useCallback((map: google.maps.Map): void => {
    if (sortedDataMap.length >= 1) {
      let ordersList: ProcessData[] = []
      let orderedProcesses: ProcessData[] = []

      for (const item of sortedDataMap) {
        const connected_orders = _.cloneDeep(item?.connected_orders || [])
        const nestedOrders = getNestedConnectedOrders(connected_orders)
        orderedProcesses = [...orderedProcesses, ...nestedOrders, item]

        if (orderedProcesses.length > 0) {
          ordersList = [...orderedProcesses]
        }
      }

      // Process ordersList only once
      ordersList = ordersList.map((process, index) => {
        const processWithID = {
          ...process,
          custom_order: (index + 1).toString(),
          longitude: process.process_long,
          latitude: process.process_lat,
          id: `${process.process_lat}-${process.process_long}`
        }
        return sortObjectProperties(processWithID)
      })

      // Group markers by location (unique lat/lng combination)
      const groupedMarkers = ordersList.reduce<ProcessData[]>((prev, curVal) => {
        const isExist = prev.findIndex((location) =>
          location.id === `${curVal.process_lat}-${curVal.process_long}`
        )

        if (isExist !== -1) {
          const existingItem = prev[isExist]
          prev[isExist] = {
            ...existingItem,
            process_position_b2c: curVal.process_position_b2c,
            list: existingItem.list ? [...existingItem.list, curVal] : [curVal],
            name: curVal.process_name
          }
        } else {
          prev.push({
            ...curVal,
            list: [curVal]
          })
        }
        return prev
      }, [] as ProcessData[])

      const mapMarkersInfo = getCenterOfMarkers(ordersList)

      setMarkersArr(groupedMarkers)
      setMarkers(ordersList)
      setProcesses(groupByProcessPosition(ordersList))

      if (mapMarkersInfo && mapMarkersInfo.center) {
        setCenter(mapMarkersInfo.center)
        setZoomLevel(mapMarkersInfo.zoomLevel)
      }
    }

    setMap(map)
  }, [sortedDataMap])

  const onUnmount = useCallback((): void => {
    setMap(null)
  }, [])

  const onMarkerClick = useCallback((position: number) => {

    if (location.latitude && location.longitude) {
      const pos = getProcessLatLng({
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude)
      })
      setCenter(pos)
    }

    setLocHover(null)

    if (infoboxTimer) {
      window.clearTimeout(infoboxTimer)
      setInfoboxTimer(null)
    }
    setLocClick(position)
  }, [infoboxTimer])

  const handleZoomChanged = useCallback((): void => {
    if (map) {
      console.log('Current Zoom level', map.getZoom())
    }
  }, [map])

  const createContentHover = useCallback((marker: ProcessData, isDrawMarker: ProcessData, isSingleMarker: boolean) => {
    if (!isDrawMarker.list) return null

    if (isSingleMarker &&
      (marker.claimed_status === E_ProcessClaimedStatus.SELF_REPORTED ||
        marker.claimed_status === E_ProcessClaimedStatus.UNCLAIMED)) {
      return isDrawMarker.list.map((poz) => (
        <Fragment key={`${poz.process_position_b2c}_${_.uniqueId('process_position_')}`}>
          <ContentMarkerUnclaimed poz={poz} />
        </Fragment>
      ))
    } else if (isSingleMarker) {
      return isDrawMarker.list.map((poz) => (
        <Fragment key={`${poz.process_position_b2c}_${_.uniqueId('process_position_')}`}>
          <ContentMarkerSingle poz={poz} />
        </Fragment>
      ))
    } else {
      return isDrawMarker.list.map((poz) => (
        <Fragment key={`${poz.process_position_b2c}_${_.uniqueId('process_position_')}`}>
          <ContentMarkerMultiple poz={poz} />
        </Fragment>
      ))
    }
  }, [])

  const createContentClick = useCallback((isDrawMarker: ProcessData) => {
    if (!isDrawMarker.list) return null

    return isDrawMarker.list.map((location) => {
      const {imgSource, imgAlt} = ProcessStatusMap[location.claimed_status || ''] || {}

      return (
        <li
          key={location.process_position_b2c}
          className={`flex-column align-items-start mb-3`}
          style={{
            cursor: isMarkerClickable(location) ? 'pointer' : 'default'
          }}
          onClick={() => {
            if (isMarkerClickable(location)) {
              onClickProcessV2(location)
            }
          }}
        >
          <h6>{translate(langData.localize, location.process_manufacturer_name || '')}</h6>
          <div className={`mlm-ifo --table mlm-ifo--${isMarkerClickable(location)}`}>
            <span className="mlm-location long-text mr-2">
              {imgSource ? (
                <img
                  className="process-status-flag"
                  src={imgSource}
                  width={36}
                  alt={imgAlt || ''}
                />
              ) : null}
              <span
                className={`process-status-${(location.claimed_status || '').toLowerCase()}`}
                dangerouslySetInnerHTML={{__html: location.custom_order || ''}}
              />
            </span>
            <span dangerouslySetInnerHTML={{__html: location.process_name || ''}} />
            <VerificationLegendBadge
              status={(location.claimed_status?.toLocaleUpperCase())}
              className={'ml-3'}
            />
          </div>
        </li>
      )
    })
  }, [langData, onClickProcessV2])

  const onCloseInfoBox = useCallback(() => {
    console.log('onCloseInfoBox')
    setLocHover(null)
    setLocClick(null)
    setInfoboxTimer(null)
  }, [])

  const onMarkerMouseEnter = useCallback((position: number) => {
    if (infoboxTimer) {
      window.clearTimeout(infoboxTimer)
    }
    if (locClick === position) {
      return
    }
    setLocHover(position)
  }, [infoboxTimer])

  const onMarkerMouseLeave = useCallback(() => {
    const timer = window.setTimeout(() => {
      setLocHover(null)
    }, TIMEOUT_WAITING)

    setInfoboxTimer(timer)
  }, [])

  const onInfoboxHover = useCallback(() => {
    if (infoboxTimer) {
      window.clearTimeout(infoboxTimer)
    }
  }, [infoboxTimer])

  const onInfoboxMouseOut = useCallback(() => {
    const timer = window.setTimeout(() => {
      setLocHover(null)
    }, TIMEOUT_FINISH)
    setInfoboxTimer(timer)
  }, [])

  const renderMarkers = useCallback(() => {
    return markers.map((loc: ProcessData, idx: number) => {
      const zIndex = 300
      const opt: MarkerOptions = {
        position: {
          lat: parseFloat(loc.latitude || loc.process_lat || '0'),
          lng: parseFloat(loc.longitude || loc.process_long || '0')
        }
      }

      const isDrawMarker = markersArr.find((mk) => mk.process_position_b2c === loc.process_position_b2c)

      if (!isDrawMarker) {
        return null
      }

      const isSingleMarker = isDrawMarker.list && isDrawMarker.list.length === 1
      const contentHover = createContentHover(loc, isDrawMarker, isSingleMarker)
      let contentClick = null

      opt.icon = {
        scaledSize: isSingleMarker ?
          new google.maps.Size(36, 48) :
          new google.maps.Size(72, 49),
        url: isSingleMarker ?
          ((loc.claimed_status === E_ProcessClaimedStatus.VERIFIED)
            ? markerIconGreenV2
            : (loc.claimed_status === E_ProcessClaimedStatus.UNCLAIMED ||
              loc.claimed_status === E_ProcessClaimedStatus.SELF_REPORTED
                ? markerIconYellowV2
                : markerIconGrayV2
            ))
          : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
        // : markerIconMultiple,
      }

      const optLabel = isSingleMarker
        ? loc.custom_order?.toString() || ''
        : formatSequentialRanges(isDrawMarker.list || [])

      if (!isSingleMarker) {

        class CustomMarkerOverlay extends google.maps.OverlayView {
          position: google.maps.LatLng
          content: string
          private contentHover: any
          private div: HTMLElement | null
          private zIndex: number
          private processList: ProcessData[]
          private markerData: ProcessData

          constructor(
            position: google.maps.LatLng,
            content: string,
            contentHover: any,
            zIndex: number = 300,
            processList: ProcessData[] = [],
            markerData: ProcessData
          ) {
            super()
            this.position = position
            this.content = content
            this.contentHover = contentHover
            this.zIndex = zIndex
            this.processList = processList
            this.markerData = markerData
          }

          onAdd() {
            // Container chính
            const div = document.createElement('div');
            div.className = 'custom-markers-container';
            div.style.zIndex = this.zIndex.toString();
            div.style.position = 'absolute';

            // Tạo container cho marker content
            const markerContentDiv = document.createElement('div');
            markerContentDiv.className = 'marker-icon-multiple';
            div.appendChild(markerContentDiv);

            // Hiển thị số thứ tự
            const markerNumberSpan = document.createElement('span');
            markerNumberSpan.className = 'marker-text';
            markerNumberSpan.innerHTML = this.content;
            markerContentDiv.appendChild(markerNumberSpan);

            // Chỉ hiển thị process number, không hiển thị process name

            const markerInfoContainer = document.createElement('div');
            markerInfoContainer.className = 'marker-info-container';
            markerInfoContainer.style.zIndex = (this.zIndex + 100).toString();

            const markerInfoContent = document.createElement('ul');
            markerInfoContent.className = 'marker-info-content';

            markerInfoContainer.appendChild(markerInfoContent);
            div.appendChild(markerInfoContainer);

            markerInfoContainer.style.display = 'none';

            div.addEventListener('mouseenter', () => {
              console.log('Marker overlay mouseenter');
              markerInfoContainer.style.display = 'block';
            });

            div.addEventListener('mouseleave', () => {
              console.log('Marker overlay mouseleave');
              markerInfoContainer.style.display = 'none';
            });

            // Thêm sự kiện cho markerContentDiv
            markerContentDiv.addEventListener('mouseenter', () => {
              console.log('Marker content mouseenter');
              markerInfoContainer.style.display = 'block';
            });

            markerContentDiv.addEventListener('mouseleave', () => {
              console.log('Marker content mouseleave');
              markerInfoContainer.style.display = 'none';
            });

            // Thêm sự kiện click để hiển thị/ẩn infobox
            let isInfoboxVisible = false;
            markerContentDiv.addEventListener('click', (e) => {
              e.stopPropagation();
              console.log('Marker content click');
              isInfoboxVisible = !isInfoboxVisible;
              markerInfoContainer.style.display = isInfoboxVisible ? 'block' : 'none';
            });

            // Đóng infobox khi click bên ngoài
            document.addEventListener('click', () => {
              if (isInfoboxVisible) {
                isInfoboxVisible = false;
                markerInfoContainer.style.display = 'none';
              }
            });

            // Render React content vào markerInfoContent
            ReactDOM.render(
              <>
                <h6 className="mb-2">{translate(langData.localize, this.markerData.process_manufacturer_name || '')}</h6>
                {this.processList.map((process, index) => (
                  <li key={`${process.process_position_b2c}_${index}`}
                      className={"fib-process-markers mb-2"}
                      style={{cursor: "pointer", borderBottom: index < this.processList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '8px'}}
                      onClick={() => onClickProcessV2(process)}
                  >
                    <div className="d-flex flex-row align-items-center">
                      <span
                        className={`fib-process-markers-status process-status-${(process.claimed_status || "").toLowerCase()}`}
                        dangerouslySetInnerHTML={{__html: process.custom_order}}/>

                      <span
                        className={`fib-process-markers-pname ml-2`}
                        dangerouslySetInnerHTML={
                          {__html: process.process_name}
                        }/>
                    </div>
                  </li>
                ))}
              </>,
              markerInfoContent
            );

            this.div = div;
            const panes = this.getPanes();
            panes?.overlayLayer.appendChild(div);
          }


          draw() {
            if (!this.div) return

            const overlayProjection = this.getProjection()
            const position = overlayProjection.fromLatLngToDivPixel(this.position)

            if (position) {
              // Điều chỉnh vị trí để marker được đặt chính giữa
              const markerWidth = this.div.offsetWidth || 72;
              this.div.style.left = (position.x - markerWidth/2) + 'px'
              this.div.style.top = (position.y - 49) + 'px'
              // Ensure z-index is applied
              this.div.style.zIndex = this.zIndex.toString()
            }
          }

          onRemove() {
            if (this.div) {
              this.div.parentNode?.removeChild(this.div)
              this.div = null
            }
          }
        }

        const markerZIndex = zIndex + idx
        const overlay = new CustomMarkerOverlay(
          new google.maps.LatLng(parseFloat(loc.latitude), parseFloat(loc.longitude)),
          optLabel,
          contentHover,
          markerZIndex,
          isDrawMarker.list || [],
          isDrawMarker
        )
        overlay.setMap(map)

      } else {
        opt.label = {
          text: optLabel,
          className: 'fib-process-marker-label'
        }
        opt.zIndex = zIndex + idx
      }

      opt.onMouseOver = () => {
        onMarkerMouseEnter(loc.process_position_b2c)
      }
      opt.onMouseOut = onMarkerMouseLeave

      if (isDrawMarker.list && isDrawMarker.list.length === 1) {
        if (isMarkerClickable(loc)) {
          opt.onClick = () => onClickProcessV2(loc)
        }
      } else if (isDrawMarker.list) {
        contentClick = createContentClick(isDrawMarker)

        opt.onClick = () => {
          onMarkerClick(loc.process_position_b2c)
        }
      }

      return (
        <Fragment key={`marker_${loc.process_position_b2c}_${idx}`}>
          <ProcessMarker
            opt={opt}
            isDrawMarker={!!isDrawMarker}
            location={loc}
            locClick={locClick}
            locHover={locHover}
            contentHover={contentHover}
            contentClick={contentClick}

            closeInfoBox={onCloseInfoBox}

            onMarkerMouseEnter={onMarkerMouseEnter}
            onMarkerMouseLeave={onMarkerMouseLeave}
            onInfoboxHover={onInfoboxHover}
            onInfoboxMouseOut={onInfoboxMouseOut}
          />
        </Fragment>
      )
    })
  }, [
    markers,
    markersArr,
    locClick,
    locHover,
    onClickProcessV2,
    createContentHover,
    createContentClick,

    onCloseInfoBox,

    onMarkerClick,
    onMarkerMouseEnter,
    onMarkerMouseLeave,
    onInfoboxHover,
    onInfoboxMouseOut
  ])

  useEffect(() => {
    return () => {
      if (infoboxTimer) {
        window.clearTimeout(infoboxTimer)
      }
    }
  }, [infoboxTimer])

  useEffect(() => {

  }, [locClick, locHover])

  if (!isLoaded) return null

  return (
    <GoogleMap
      mapContainerStyle={{
        width: '100%',
        height: '500px'
      }}
      zoom={zoomLevel}
      center={center || undefined}
      mapTypeId={window.google.maps.MapTypeId.ROADMAP}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onZoomChanged={handleZoomChanged}
    >
      {processes.map((groups, index) => (
        <ProcessesMarkers
          key={`curve-${index}`}
          data={groups}
        />
      ))}

      {renderMarkers()}
    </GoogleMap>
  )
}

export default React.memo(POSMapV2)